import { useLingui } from '@lingui/react/macro'
import { Button } from 'designSystem/components/Button'
import {
  Combobox,
  ComboboxContent,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
} from 'designSystem/components/Combobox'
import { X } from 'lucide-react'
import { type ReactElement, useRef, useState } from 'react'

import {
  LotoComboboxClearSlot,
  LotoComboboxSlot,
  LotoComboboxText,
} from './lotoPagePanel.styles'

type LotoPresetComboboxProps = {
  label: string
  clearLabel: string
  placeholder: string
  value: string
  options: string[]
  onChange: (value: string) => void
}

function isPreset(options: string[], text: string): boolean {
  const lowered = text.toLowerCase()
  return options.some((option) => option.toLowerCase() === lowered)
}

function focusTrigger(slot: HTMLDivElement | null): void {
  const trigger = slot?.firstElementChild
  if (trigger instanceof HTMLButtonElement) trigger.focus()
}

export function LotoPresetCombobox({
  label,
  clearLabel,
  placeholder,
  value,
  options,
  onChange,
}: LotoPresetComboboxProps): ReactElement {
  const { t } = useLingui()
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const slotRef = useRef<HTMLDivElement>(null)
  const typed = search.trim()
  const changeOpen = (next: boolean): void => {
    setOpen(next)
    if (!next) setSearch('')
  }
  const clear = (): void => {
    onChange('')
    focusTrigger(slotRef.current)
  }
  return (
    <Combobox
      value={value}
      open={open}
      onOpenChange={changeOpen}
      onValueChange={onChange}>
      <LotoComboboxSlot ref={slotRef}>
        <ComboboxTrigger
          size='sm'
          aria-label={label}
          placeholder={<LotoComboboxText>{placeholder}</LotoComboboxText>}>
          {value ? (
            <LotoComboboxText $clearable>{value}</LotoComboboxText>
          ) : undefined}
        </ComboboxTrigger>
        {value && (
          <LotoComboboxClearSlot>
            <Button
              variant='ghostMuted'
              size='icon-sm'
              aria-label={clearLabel}
              onClick={clear}>
              <X />
            </Button>
          </LotoComboboxClearSlot>
        )}
      </LotoComboboxSlot>
      <ComboboxContent>
        <ComboboxInput
          aria-label={label}
          placeholder={t`Choose or type…`}
          value={search}
          onValueChange={setSearch}
        />
        <ComboboxList>
          {typed && !isPreset(options, typed) && (
            <ComboboxGroup>
              <ComboboxItem value={typed}>{t`Use “${typed}”`}</ComboboxItem>
            </ComboboxGroup>
          )}
          <ComboboxGroup>
            {options.map((option) => (
              <ComboboxItem key={option} value={option}>
                {option}
              </ComboboxItem>
            ))}
          </ComboboxGroup>
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  )
}
